import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { SEO } from "@/components/SEO";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Calendar, Clock, AlertTriangle, ExternalLink, BookOpen, ArrowRight, Smartphone, Globe } from "lucide-react";
import { Link } from "react-router-dom";

const SunriseCalendarHistory = () => {
  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "mainEntityOfPage": { "@type": "WebPage", "@id": "https://csw.live/sunrise-calendar/history/" },
    "headline": "Sunrise Calendar: The Best Calendar App Ever Made",
    "description": "The history of Sunrise Calendar, the beautifully designed app that Microsoft acquired for $100 million and discontinued in 2016.",
    "author": { "@type": "Organization", "name": "CSW Team", "url": "https://csw.live" },
    "publisher": { "@type": "Organization", "name": "CSW - Chill Study Work", "url": "https://csw.live" },
    "datePublished": "2025-01-11",
    "dateModified": "2025-01-11"
  };

  return (
    <div className="min-h-screen bg-background">
      <SEO 
        title="Sunrise Calendar History - The Best Calendar App Ever"
        description="A retrospective on Sunrise Calendar - the beautifully designed app that Microsoft acquired for $100 million and discontinued in 2016."
        keywords="sunrise calendar history, sunrise app shutdown, microsoft acquisition, best calendar app, outlook calendar"
        canonicalUrl="/sunrise-calendar/history/"
      />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }} />
      <Header />
      
      <main className="pt-20">
        <section className="relative py-20 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-primary/10 via-background to-accent/10" />
          <div className="container mx-auto px-4 relative z-10">
            <nav className="mb-8">
              <ol className="flex items-center gap-2 text-sm text-muted-foreground">
                <li><Link to="/" className="hover:text-primary">Home</Link></li>
                <li>/</li>
                <li><Link to="/sunrise-calendar/" className="hover:text-primary">Sunrise Calendar</Link></li>
                <li>/</li>
                <li className="text-foreground">History</li>
              </ol>
            </nav>

            <div className="max-w-4xl mx-auto text-center">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 text-primary text-sm font-medium mb-6">
                <Calendar className="w-4 h-4" />
                A Retrospective • 2013-2016
              </div>
              
              <h1 className="text-4xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-foreground via-primary to-foreground bg-clip-text text-transparent">
                The Sunrise Calendar Story
              </h1>
              
              <p className="text-xl text-muted-foreground mb-8">
                The calendar app that was hailed as "the best ever" before Microsoft acquired and shut it down.
              </p>
            </div>
          </div>
        </section>

        <section className="py-16 bg-muted/30">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto">
              <Card className="border-primary/20 bg-gradient-to-br from-background to-primary/5">
                <CardContent className="p-8">
                  <p className="text-lg text-muted-foreground">
                    Sunrise was a beautifully designed calendar app founded by Pierre Valade and Jeremy Le Van (former Foursquare designers) in New York. It first launched in early 2013 and quickly gained over <span className="text-foreground font-semibold">5 million users</span>.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        <section className="py-20">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <div className="flex items-center gap-3 mb-6">
                <div className="p-3 rounded-xl bg-primary/10">
                  <BookOpen className="w-6 h-6 text-primary" />
                </div>
                <h2 className="text-3xl font-bold">Why It Was "The Best"</h2>
              </div>
              
              <div className="space-y-6">
                <p className="text-muted-foreground text-lg">
                  Sunrise's mission was simple: <span className="text-foreground font-medium">combine all your calendars in one gorgeous interface</span>. It integrated Google Calendar, iCloud, Exchange, Facebook events, LinkedIn, and more.
                </p>

                <Card className="border-border/50">
                  <CardContent className="p-6">
                    <h3 className="font-semibold text-foreground mb-4">Standout Features</h3>
                    <ul className="grid md:grid-cols-2 gap-3 text-muted-foreground">
                      <li className="flex items-center gap-2"><Globe className="w-4 h-4 text-primary" /> Multi-calendar sync (work + personal + social)</li>
                      <li className="flex items-center gap-2"><Clock className="w-4 h-4 text-primary" /> Time zone smartness for travelers</li>
                      <li className="flex items-center gap-2"><Smartphone className="w-4 h-4 text-primary" /> Third-party integrations (Evernote, TripIt, Wunderlist)</li>
                      <li className="flex items-center gap-2"><Calendar className="w-4 h-4 text-primary" /> Natural language input</li>
                    </ul>
                  </CardContent>
                </Card>

                <p className="text-muted-foreground text-lg">
                  Little details like event icons (adding a 🎂 for birthdays) endeared it to users. TechCrunch ran a eulogy titled <span className="italic">"Sunrise was the best calendar app ever and now I feel lost."</span>
                </p>

                <a href="https://venturebeat.com/ai/microsoft-confirms-acquisition-of-hot-calendar-app-sunrise" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-muted hover:bg-muted/80 text-sm">
                  <ExternalLink className="w-4 h-4" />
                  VentureBeat: Microsoft Acquires Sunrise
                </a>
              </div>
            </div>
          </div>
        </section>

        <section className="py-20 bg-gradient-to-br from-primary/5 via-background to-accent/5">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <div className="flex items-center gap-3 mb-6">
                <div className="p-3 rounded-xl bg-destructive/10">
                  <AlertTriangle className="w-6 h-6 text-destructive" />
                </div>
                <h2 className="text-3xl font-bold">The Shutdown</h2>
              </div>

              <div className="space-y-6 text-lg text-muted-foreground">
                <p>
                  In <span className="text-foreground font-semibold">February 2015</span>, Microsoft acquired Sunrise for reportedly at least $100 million. Microsoft was scooping up top mobile productivity apps to invigorate Outlook.
                </p>

                <Card className="border-destructive/30 bg-destructive/5">
                  <CardContent className="p-6">
                    <p className="text-foreground font-medium text-xl mb-4">The End</p>
                    <p className="text-muted-foreground">
                      The shutdown date was set for <span className="font-semibold">August 31, 2016</span>. Microsoft removed Sunrise from app stores and turned off sync services. The reason: <span className="italic">"we're bringing the magic of Sunrise to Outlook, so Sunrise itself will be sunset."</span>
                    </p>
                  </CardContent>
                </Card>

                <p>
                  Users were not thrilled. Many didn't want to switch to Outlook, which they felt was clunkier despite improvements. Certain beloved features like Sunrise's unique widgets never made it to Outlook.
                </p>

                <div className="flex flex-wrap gap-3">
                  <a href="https://www.theverge.com/2016/5/11/11657960/microsoft-shutting-down-sunrise-app" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-muted hover:bg-muted/80 text-sm">
                    <ExternalLink className="w-4 h-4" />
                    The Verge: Microsoft Kills Sunrise
                  </a>
                  <a href="https://techcrunch.com/2016/08/31/sunrise-was-the-best-calendar-app-ever-and-now-i-feel-lost/" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-muted hover:bg-muted/80 text-sm">
                    <ExternalLink className="w-4 h-4" />
                    TechCrunch: Best Calendar App Ever
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="py-20">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-3xl font-bold mb-6">Looking for Productivity Tools?</h2>
            <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
              CSW helps you execute on your schedule with task management, timers, and virtual study rooms.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button size="lg" asChild>
                <a href="https://app.csw.live" target="_blank" rel="noopener noreferrer">
                  Try CSW Free <ArrowRight className="ml-2 w-5 h-5" />
                </a>
              </Button>
              <Button size="lg" variant="outline" asChild>
                <Link to="/sunrise-calendar/">View Sunrise Alternative</Link>
              </Button>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default SunriseCalendarHistory;
